import React, { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Descriptions,
  Form,
  Input,
  Modal,
  Select,
  Statistic,
  Switch,
  Tag,
  message,
} from 'antd';
import { useMutation } from '@apollo/client';
import { hasResource } from 'utils/resourceAuth';
import { ResourceCode } from 'constants/resourceCodes';
import {
  FIX_ORDER_WMS_COLUMN,
  FixOrderWmsColumnResponse,
  FixOrderWmsColumnVariables,
  ORDER_WMS_FIXABLE_COLUMNS,
  OrderWmsColumnFixItemInput,
  OrderWmsFixableColumnValue,
  TT_BIZ_TYPE_ALLOWED_VALUES,
} from './graphql/orderWmsColumnFixGraphql';

type FixResult = FixOrderWmsColumnResponse['fixOrderWmsColumn'];

interface ParseResult {
  items: OrderWmsColumnFixItemInput[];
  errors: string[];
}

/**
 * 解析粘贴文本：每行 `order_id<分隔符>value`，分隔符兼容逗号 / Tab / 空格（Excel 复制多为 Tab）。
 * 过滤空行、trim；order_id 必须为正整数；同一 order_id 的冲突交由后端校验并报错。
 */
function parsePastedItems(raw: string): ParseResult {
  const items: OrderWmsColumnFixItemInput[] = [];
  const errors: string[] = [];
  const lines = raw.split(/\r?\n/);
  lines.forEach((line, idx) => {
    const trimmed = line.trim();
    if (trimmed.length === 0) return;
    const parts = trimmed.split(/[,\t\s]+/).filter((p) => p.length > 0);
    if (parts.length < 2) {
      errors.push(`第 ${idx + 1} 行格式错误（需 order_id 和 value 两列）：${trimmed}`);
      return;
    }
    const orderId = Number(parts[0]);
    if (!Number.isInteger(orderId) || orderId <= 0) {
      errors.push(`第 ${idx + 1} 行 order_id 非法：${parts[0]}`);
      return;
    }
    items.push({ orderId, value: parts[1] });
  });
  return { items, errors };
}

const OrderWmsColumnFixPage: React.FC = () => {
  const canOperate = hasResource([ResourceCode.menu.OPS_TOOL_WMS]);
  const [form] = Form.useForm();
  const [result, setResult] = useState<FixResult | null>(null);
  const [trigger, { loading }] = useMutation<FixOrderWmsColumnResponse, FixOrderWmsColumnVariables>(
    FIX_ORDER_WMS_COLUMN,
  );

  const handleTrigger = async () => {
    if (!canOperate) {
      message.error('无操作权限');
      return;
    }
    const values = await form.validateFields();
    const column: OrderWmsFixableColumnValue = values.column;
    const onlyWhenNull: boolean = values.onlyWhenNull;
    const dryRun: boolean = values.dryRun;

    const { items, errors } = parsePastedItems(values.rawText ?? '');
    if (errors.length > 0) {
      message.error(`共 ${errors.length} 行解析失败，请修正后重试。首条：${errors[0]}`);
      return;
    }
    if (items.length === 0) {
      message.error('请粘贴至少一行 order_id 与 value');
      return;
    }
    if (items.length > 10000) {
      message.error(`单次最多 10000 行，当前 ${items.length} 行，请分批提交`);
      return;
    }
    // tt_biz_type 列前端预校验，避免整批打到后端才报错
    if (column === 'TT_BIZ_TYPE') {
      const allowed = TT_BIZ_TYPE_ALLOWED_VALUES.map((v) => v.toUpperCase());
      const badRows = items.filter((it) => !allowed.includes(it.value.toUpperCase()));
      if (badRows.length > 0) {
        message.error(`tt_biz_type 仅允许 CBT/FBT，有 ${badRows.length} 行非法（如 order_id=${badRows[0].orderId}）`);
        return;
      }
    }

    const columnLabel = ORDER_WMS_FIXABLE_COLUMNS.find((c) => c.value === column)?.column ?? column;

    Modal.confirm({
      title: dryRun ? '确认执行预演（dryRun）' : '确认执行正式回写',
      content: (
        <div>
          <div>
            目标列：<b>{columnLabel}</b>，共 <b>{items.length}</b> 行（去重前）。
          </div>
          <div>
            写入模式：
            {onlyWhenNull ? (
              <span>仅当该列当前为空（NULL）时写入，不覆盖已有值。</span>
            ) : (
              <span style={{ color: '#cf1322', fontWeight: 600 }}>强制覆盖模式：无论是否已有值都会被改写！请再三确认。</span>
            )}
          </div>
          {dryRun ? (
            <div>本次为预演，仅统计将命中/未命中的数量，不写库。</div>
          ) : (
            <div style={{ color: '#cf1322' }}>本次为正式回写，将真实修改数据库，请确认数据无误。</div>
          )}
        </div>
      ),
      okText: '确认触发',
      cancelText: '取消',
      okButtonProps: dryRun ? undefined : { danger: true },
      onOk: async () => {
        try {
          const { data } = await trigger({
            variables: { column, items, onlyWhenNull, dryRun },
          });
          const payload = data?.fixOrderWmsColumn ?? null;
          setResult(payload);
          if (dryRun) {
            message.success('预演完成，请查看命中/未命中统计');
          } else {
            message.success(`回写完成，实际更新 ${payload?.updatedCount ?? 0} 行`);
          }
        } catch (e: any) {
          message.error(e?.message ?? '触发失败');
        }
      },
    });
  };

  return (
    <Card title="order_wms 数据订正">
      <Alert
        type="info"
        showIcon
        className="mb-4"
        message="用途与操作说明"
        description={
          <div>
            <p>
              <b>用途：</b>按运维提供的 <code>(order_id, value)</code> 成对数据，批量订正 <code>order_wms</code>{' '}
              表的<b>单个字段</b>（首例场景：回填历史 <code>tt_biz_type</code> 为空的订单）。工具完全以你粘贴的值为准，<b>不做任何后端推导</b>。
            </p>
            <p>
              <b>操作步骤：</b>
            </p>
            <ol style={{ paddingLeft: 20, marginBottom: 8 }}>
              <li>
                <b>选择目标列</b>：只能选下拉里的白名单列（出于安全，不开放任意列）。
              </li>
              <li>
                <b>是否仅校验空值才修改</b>：
                <br />· 勾选（默认）= 仅当该列当前为<b>空（NULL）</b>时才写入，<b>不覆盖</b>已有值；
                <br />· 不勾选 = <span style={{ color: '#cf1322', fontWeight: 600 }}>强制覆盖</span>已有值（高危，请谨慎）。
              </li>
              <li>
                <b>粘贴数据</b>：每行一条，格式为 <code>order_id 分隔符 value</code>，分隔符支持逗号 / Tab / 空格（可直接从 Excel 两列复制）。
                同一批可以混不同 value（如同时有 CBT 和 FBT）。选中 <code>tt_biz_type</code> 时 value 仅允许 <b>CBT / FBT</b>（大小写不敏感）。
              </li>
              <li>
                <b>建议先开启「预演(dryRun)」</b>试跑：只统计会命中/未命中多少，不写库；确认无误后关闭预演再正式回写。
              </li>
            </ol>
            <p style={{ marginBottom: 0 }}>
              <b>数量限制：</b>单次最多 <b>10000 行</b>，更多请分多批粘贴提交。
              <br />
              <b>结果说明：</b>「命中数」= 本次会被订正的订单；「未命中清单」= 未被修改的 order_id（不存在 / 已删除 / 或勾选空值校验时该列已有值），可复制核对。
            </p>
          </div>
        }
      />

      <Form
        form={form}
        layout="vertical"
        initialValues={{ column: 'TT_BIZ_TYPE', onlyWhenNull: true, dryRun: true, rawText: '' }}
      >
        <Form.Item name="column" label="目标列" rules={[{ required: true, message: '请选择要订正的列' }]}>
          <Select
            style={{ maxWidth: 420 }}
            options={ORDER_WMS_FIXABLE_COLUMNS.map((c) => ({ value: c.value, label: c.label }))}
          />
        </Form.Item>

        <Form.Item name="onlyWhenNull" valuePropName="checked">
          <Checkbox>
            仅校验空值才修改（勾选=只补空、不覆盖已有值；取消勾选=强制覆盖，高危）
          </Checkbox>
        </Form.Item>

        <Form.Item
          name="rawText"
          label="订正数据（每行：order_id 逗号/Tab/空格 value）"
          rules={[{ required: true, message: '请粘贴订正数据' }]}
        >
          <Input.TextArea
            rows={10}
            placeholder={'例如：\n1001,FBT\n1002,CBT\n1003\tFBT'}
            style={{ fontFamily: 'monospace' }}
          />
        </Form.Item>

        <Form.Item name="dryRun" label="预演(dryRun)" valuePropName="checked">
          <Switch checkedChildren="是" unCheckedChildren="否" />
        </Form.Item>

        <Form.Item>
          <Button type="primary" loading={loading} disabled={!canOperate} onClick={handleTrigger}>
            触发订正
          </Button>
        </Form.Item>
      </Form>

      {result && (
        <>
          <Card size="small" className="mb-4" title="本次结果统计">
            <div className="flex gap-8">
              <Statistic title="请求订单数（去重后）" value={result.requestedCount} />
              <Statistic title="命中数（会被订正）" value={result.fixableCount} valueStyle={{ color: '#1677ff' }} />
              <Statistic title="实际写入行数" value={result.updatedCount} valueStyle={{ color: '#3f8600' }} />
              <Statistic title="未命中数" value={result.notMatchedOrderIds.length} valueStyle={{ color: '#cf1322' }} />
              <Statistic title="失败批次订单数" value={result.failedOrderIds.length} valueStyle={{ color: '#cf1322' }} />
            </div>
          </Card>

          <Descriptions bordered size="small" column={1} title="明细（可复制核对）">
            <Descriptions.Item label={`未命中 order_id（${result.notMatchedOrderIds.length}）`}>
              {result.notMatchedOrderIds.length > 0 ? (
                <Input.TextArea
                  readOnly
                  autoSize={{ minRows: 1, maxRows: 8 }}
                  value={result.notMatchedOrderIds.join(',')}
                />
              ) : (
                <Tag color="green">无</Tag>
              )}
            </Descriptions.Item>
            <Descriptions.Item label={`写入失败 order_id（${result.failedOrderIds.length}）`}>
              {result.failedOrderIds.length > 0 ? (
                <Input.TextArea
                  readOnly
                  autoSize={{ minRows: 1, maxRows: 8 }}
                  value={result.failedOrderIds.join(',')}
                />
              ) : (
                <Tag color="green">无</Tag>
              )}
            </Descriptions.Item>
          </Descriptions>
        </>
      )}
    </Card>
  );
};

export default OrderWmsColumnFixPage;
